import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CopyableOrderId } from "@/shared/components/CopyableOrderId";
import { OrderAddItemsPicker } from "@/shared/components/OrderAddItemsPicker";
import { Order, ProductUI } from "@/shared/types";
import { useCart } from "@/app/providers/CartProvider";
import "./styles/OrderDetails.scss";
import { districtsApi, type CityDistrict } from "@/shared/api/districtsApi";
import { cityApi, type City } from "@/shared/api/cityApi";
import { openPaymentUrl } from "@/shared/utils/openPaymentUrl";
import { resolveRuntimeMessenger } from "@/shared/utils/runtimeMessenger";
import { showAppConfirm } from "@/shared/utils/appDialog";
import { formatPhotoUrl } from "@/shared/utils/formatter";
import {
  formatUnitPriceLabel,
  getLineTotal,
  resolveProductPrice,
} from "@/shared/utils/productPricing";

interface OrderDetailsProps {
  orderId: string | null;
  onClose: () => void;
  fetchOrderById: (id: string) => Promise<Order | null>;
  updateOrderDelivery: (
    orderId: string,
    payload: {
      pickup: boolean;
      deliveryAddress?: string;
      districtId?: string;
      outletId?: string;
      cashId?: string;
      comment?: string;
    }
  ) => Promise<Order>;
  updateOrderItems: (
    orderId: string,
    items: Array<{
      dishLineId: string;
      quantity: number;
    }>
  ) => Promise<Order>;
  addOrderItems: (
    orderId: string,
    items: Array<{
      productId: string;
      quantity: number;
    }>
  ) => Promise<Order>;
  updateOrderPaymentMethod: (
    orderId: string,
    paymentMethod: "yookassa" | "cash"
  ) => Promise<Order>;
  cancelOrder: (orderId: string) => Promise<Order>;
  payOrder: (
    orderId: string
  ) => Promise<{ paymentUrl: string; amount: number; reused: boolean }>;
}

const statusColors = {
  pending: { bg: "#fff3e0", color: "#e65100", text: "Ожидает оплаты" },
  processing: { bg: "#e3f2fd", color: "#0d47a1", text: "Готовится" },
  completed: { bg: "#e8f5e8", color: "#1b5e20", text: "Оплачен" },
  cancelled: { bg: "#ffebee", color: "#b71c1c", text: "Отменён" },
};

const NO_IMAGE = "/icons/no-image.svg";

const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
  event.currentTarget.src = NO_IMAGE;
};

const paymentMethods: Record<string, string> = {
  yookassa: "Онлайн-оплата",
  card: "Банковская карта",
  cash: "Оплата при получении",
};

function normalizeEditableAddress(order: Order): string {
  const rawAddress = order.deliveryAddress || "";
  const districtName = order.districtName?.trim();
  if (!districtName) return rawAddress;

  const withComma = `${districtName}, `;
  if (rawAddress.startsWith(withComma)) {
    return rawAddress.slice(withComma.length);
  }
  if (rawAddress === districtName) {
    return "";
  }
  return rawAddress;
}

function normalizePaymentMethod(method: string): "yookassa" | "cash" {
  return method === "yookassa" ? "yookassa" : "cash";
}

export const OrderDetails: React.FC<OrderDetailsProps> = ({
  orderId,
  onClose,
  fetchOrderById,
  updateOrderDelivery,
  updateOrderItems,
  addOrderItems,
  updateOrderPaymentMethod,
  cancelOrder,
  payOrder,
}) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingAdds, setPendingAdds] = useState<
    Array<{ product: ProductUI; quantity: number }>
  >([]);

  const [pickup, setPickup] = useState(true);
  const [address, setAddress] = useState("");
  const [comment, setComment] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [districts, setDistricts] = useState<CityDistrict[]>([]);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [districtsError, setDistrictsError] = useState<string | null>(null);
  const [cities, setCities] = useState<City[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [selectedCashId, setSelectedCashId] = useState("");
  const [paymentMethodDraft, setPaymentMethodDraft] = useState<"yookassa" | "cash">(
    "cash"
  );
  const [itemQty, setItemQty] = useState<Record<string, number>>({});
  const { clearPendingOrderDraft, pendingOrderDraft, savePendingOrderDraft } =
    useCart();

  const isEditable = Boolean(order?.editable);
  const isPaid = order?.status === "completed";
  const canPay = Boolean(order?.canPayOnline);
  const selectedCity = useMemo(
    () => cities.find((c) => c.id === selectedCashId) ?? null,
    [cities, selectedCashId]
  );
  const outletId = selectedCity?.outletId || order?.outletId;
  const onlyPickup = Boolean(
    selectedCity?.onlyPickup ?? order?.outletOnlyPickup
  );
  const cashPaymentLabel = pickup
    ? "Оплата при получении"
    : "Оплата курьеру";

  const getItemKey = (item: Order["items"][number]) =>
    item.dishLineId || item.product.id;

  /** qty из локального стейта: ключ мог быть и dishLineId, и product.id */
  const resolveItemQty = useCallback(
    (item: Order["items"][number]) => {
      if (item.dishLineId && itemQty[item.dishLineId] !== undefined) {
        return itemQty[item.dishLineId];
      }
      const byProduct = itemQty[String(item.product.id)];
      if (byProduct !== undefined) {
        return byProduct;
      }
      const byKey = itemQty[getItemKey(item)];
      if (byKey !== undefined) {
        return byKey;
      }
      return item.quantity;
    },
    [itemQty]
  );

  const applyOrderToForm = useCallback((data: Order) => {
    setOrder(data);
    setPickup(Boolean(data.pickup));
    setAddress(normalizeEditableAddress(data));
    setComment(data.comment || "");
    setDistrictId(data.districtId || "");
    setSelectedCashId(data.cashId || "");
    setPaymentMethodDraft(normalizePaymentMethod(data.paymentMethod));
    setItemQty(
      Object.fromEntries(
        data.items.map((item) => [getItemKey(item), item.quantity])
      )
    );
    setPendingAdds([]);
  }, []);

  const loadOrder = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchOrderById(orderId);
      if (!data) {
        setError("Заказ не найден");
        setOrder(null);
        return;
      }
      applyOrderToForm(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Не удалось загрузить заказ"
      );
    } finally {
      setLoading(false);
    }
  }, [orderId, fetchOrderById, applyOrderToForm]);

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      setActionError(null);
      setActionNotice(null);
      setPendingAdds([]);
      setPickerOpen(false);
      return;
    }
    void loadOrder();
  }, [orderId, loadOrder]);

  useEffect(() => {
    if (!orderId || !isEditable) {
      setCities([]);
      return;
    }
    let cancelled = false;
    setCitiesLoading(true);
    cityApi
      .getCities()
      .then((data) => {
        if (cancelled) return;
        setCities(data);
        setSelectedCashId((prev) => {
          if (prev && data.some((c) => c.id === prev)) return prev;
          const byOutlet = data.find(
            (c) => c.outletId && c.outletId === order?.outletId
          );
          return byOutlet?.id || data[0]?.id || prev;
        });
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      })
      .finally(() => {
        if (!cancelled) setCitiesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, isEditable, order?.outletId]);

  useEffect(() => {
    if (!outletId || pickup) {
      setDistricts([]);
      setDistrictsError(null);
      return;
    }
    let cancelled = false;
    setDistrictsLoading(true);
    setDistrictsError(null);
    districtsApi
      .getDistricts(outletId)
      .then((data) => {
        if (!cancelled) {
          setDistricts(data);
          setDistrictsError(null);
          setDistrictId((prev) =>
            prev && data.some((d) => d.id === prev) ? prev : ""
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDistricts([]);
          setDistrictsError("Не удалось загрузить список районов");
        }
      })
      .finally(() => {
        if (!cancelled) setDistrictsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [outletId, pickup]);

  useEffect(() => {
    if (onlyPickup) {
      setPickup(true);
    }
  }, [onlyPickup, order?.id, selectedCashId]);

  const paymentDirty = useMemo(() => {
    if (!order || !isEditable) return false;
    return paymentMethodDraft !== normalizePaymentMethod(order.paymentMethod);
  }, [order, isEditable, paymentMethodDraft]);

  const deliveryDirty = useMemo(() => {
    if (!order || !isEditable) return false;
    const currentCashId = order.cashId || "";
    const currentOutletId = order.outletId || "";
    const currentDistrictId = order.districtId || "";
    const currentAddress = normalizeEditableAddress(order);
    const currentComment = order.comment || "";

    return (
      Boolean(pickup) !== Boolean(order.pickup) ||
      selectedCashId !== currentCashId ||
      (outletId || "") !== currentOutletId ||
      (!pickup && districtId !== currentDistrictId) ||
      (!pickup && address.trim() !== currentAddress.trim()) ||
      comment.trim() !== currentComment.trim()
    );
  }, [
    order,
    isEditable,
    pickup,
    selectedCashId,
    outletId,
    districtId,
    address,
    comment,
  ]);

  const itemsDirty = useMemo(() => {
    if (!order || !isEditable) return false;
    return order.items.some(
      (item) => resolveItemQty(item) !== item.quantity
    );
  }, [order, isEditable, resolveItemQty]);

  const pendingAddsDirty = pendingAdds.length > 0;
  const isDirty = paymentDirty || deliveryDirty || itemsDirty || pendingAddsDirty;

  const remainingItemsCount = useMemo(() => {
    if (!order) return 0;
    const kept = order.items.filter(
      (item) => resolveItemQty(item) > 0
    ).length;
    return kept + pendingAdds.length;
  }, [order, resolveItemQty, pendingAdds]);

  const requestClose = useCallback(() => {
    if (saving || paying || cancelling) return;
    // Ошибка загрузки — сразу выходим, без confirm (иначе ловушка deep-link).
    if (error && !order) {
      onClose();
      return;
    }
    void (async () => {
      if (isDirty) {
        const leave = await showAppConfirm(
          "Есть несохранённые изменения. Закрыть без сохранения?",
          {
            title: "Закрыть заказ?",
            confirmLabel: "Закрыть",
            cancelLabel: "Остаться",
          }
        );
        if (!leave) return;
      }
      onClose();
    })();
  }, [saving, paying, cancelling, isDirty, error, order, onClose]);

  const sameProductId = (a: string | number, b: string | number) =>
    String(a) === String(b);

  const handlePickProduct = useCallback(
    (product: ProductUI, quantity: number) => {
      if (!order) return;

      const existing = order.items.find((item) =>
        sameProductId(item.product.id, product.id)
      );
      if (existing) {
        const key = getItemKey(existing);
        setItemQty((prev) => ({
          ...prev,
          [key]: Math.max(0, prev[key] ?? existing.quantity) + quantity,
        }));
        return;
      }

      setPendingAdds((prev) => {
        const idx = prev.findIndex((item) =>
          sameProductId(item.product.id, product.id)
        );
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            quantity: next[idx].quantity + quantity,
          };
          return next;
        }
        return [...prev, { product, quantity }];
      });
    },
    [order]
  );

  const handleSaveAll = async (): Promise<Order | null> => {
    if (!order || !isDirty) return order;

    if (itemsDirty && remainingItemsCount < 1) {
      setActionError("В заказе должна остаться хотя бы одна позиция");
      return null;
    }

    // Валидация доставки до любых API-вызовов — иначе payment может уйти, а delivery упасть.
    if (deliveryDirty) {
      if (!selectedCashId || !outletId) {
        setActionError("Выберите точку");
        return null;
      }
      if (!pickup && (districtsLoading || districtsError)) {
        setActionError(
          districtsError || "Дождитесь загрузки районов доставки"
        );
        return null;
      }
      if (!pickup && districts.length > 0 && !districtId) {
        setActionError("Выберите район доставки");
        return null;
      }
      if (!pickup && !address.trim()) {
        setActionError("Укажите адрес доставки");
        return null;
      }
    }

    setSaving(true);
    setActionError(null);
    setActionNotice(null);

    try {
      let current = order;

      if (paymentDirty) {
        current = await updateOrderPaymentMethod(current.id, paymentMethodDraft);
      }

      if (deliveryDirty) {
        current = await updateOrderDelivery(current.id, {
          pickup,
          deliveryAddress: pickup ? "" : address,
          districtId: !pickup && districtId ? districtId : undefined,
          outletId: outletId!,
          cashId: selectedCashId,
          comment: comment.trim(),
        });
      }

      if (pendingAdds.length > 0) {
        current = await addOrderItems(
          current.id,
          pendingAdds.map((item) => ({
            productId: item.product.id,
            quantity: item.quantity,
          }))
        );
      }

      if (itemsDirty) {
        // Берём исходный состав заказа (до add_items): пользователь менял qty
        // именно по этим строкам. После add_items ключи/состав current могут
        // отличаться — из‑за этого удаление тихо пропускалось.
        const changedWithoutLineId = order.items.some((item) => {
          const qty = resolveItemQty(item);
          return qty !== item.quantity && !item.dishLineId;
        });
        if (changedWithoutLineId) {
          setActionError(
            "Не удалось сохранить состав: у позиции нет идентификатора строки. Обновите заказ и попробуйте снова."
          );
          return null;
        }

        const items = order.items
          .map((item) => {
            const qty = resolveItemQty(item);
            if (!item.dishLineId || qty === item.quantity) {
              return null;
            }
            return { dishLineId: item.dishLineId, quantity: qty };
          })
          .filter((item): item is { dishLineId: string; quantity: number } =>
            Boolean(item)
          );

        if (items.length === 0) {
          setActionError("Не удалось сохранить изменение состава заказа");
          return null;
        }

        current = await updateOrderItems(current.id, items);
      }

      applyOrderToForm(current);
      setActionNotice("Изменения сохранены");
      return current;
    } catch (err) {
      // Подтянуть уже сохранённые шаги, чтобы UI не врал после частичного save.
      try {
        const refreshed = await fetchOrderById(order.id);
        if (refreshed) {
          applyOrderToForm(refreshed);
        }
      } catch {
        /* ignore refresh errors */
      }
      setActionError(
        err instanceof Error ? err.message : "Не удалось сохранить изменения"
      );
      return null;
    } finally {
      setSaving(false);
    }
  };

  const startPayment = async (targetOrder: Order) => {
    setPaying(true);
    setActionError(null);
    try {
      const result = await payOrder(targetOrder.id);
      savePendingOrderDraft({
        orderId: targetOrder.id,
        paymentUrl: result.paymentUrl,
        items: targetOrder.items.map((item) => ({
          product: item.product,
          quantity: item.quantity,
        })),
      });
      const messengerKind = resolveRuntimeMessenger().kind;
      openPaymentUrl(result.paymentUrl);
      if (messengerKind === "telegram") {
        setActionNotice("Переходим к оплате...");
        return;
      }
      setActionNotice(
        result.reused
          ? "Открыли актуальную ссылку на оплату."
          : "Сформировали новую ссылку на оплату."
      );
      await loadOrder();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Не удалось запустить оплату";
      if (/failed to fetch|networkerror|load failed/i.test(message)) {
        setActionNotice("Переходим к оплате...");
        return;
      }
      setActionError(message);
    } finally {
      setPaying(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!order || !isEditable) return;
    if (isDirty) {
      setActionError("Сначала сохраните или отмените изменения");
      return;
    }
    const confirmed = await showAppConfirm(
      "Отменить заказ? Это действие нельзя отменить.",
      {
        title: "Отмена заказа",
        confirmLabel: "Отменить заказ",
        cancelLabel: "Не отменять",
        destructive: true,
      }
    );
    if (!confirmed) return;

    setCancelling(true);
    setActionError(null);
    setActionNotice(null);
    try {
      const updated = await cancelOrder(order.id);
      if (
        pendingOrderDraft?.orderId &&
        pendingOrderDraft.orderId === order.id
      ) {
        clearPendingOrderDraft();
      }
      applyOrderToForm(updated);
      setActionNotice("Заказ отменён");
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "Не удалось отменить заказ"
      );
    } finally {
      setCancelling(false);
    }
  };

  const handlePay = async () => {
    if (!order) return;
    if (isDirty) {
      setActionError("Сначала сохраните изменения");
      return;
    }
    await startPayment(order);
  };

  const handleSaveAndPay = async () => {
    if (!order || !canPay) return;
    let current: Order | null = order;
    if (isDirty) {
      current = await handleSaveAll();
      if (!current) return;
    }
    await startPayment(current);
  };

  if (!orderId) return null;

  const status =
    order &&
    (statusColors[order.status as keyof typeof statusColors] ||
      statusColors.pending);

  const date = order
    ? new Date(order.date).toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  const displayTotal = order
    ? order.items.reduce((sum, item) => {
        const key = getItemKey(item);
        const qty = itemQty[key] ?? item.quantity;
        if (qty <= 0) return sum;
        return sum + getLineTotal(item.product, qty);
      }, 0) +
      pendingAdds.reduce(
        (sum, item) => sum + getLineTotal(item.product, item.quantity),
        0
      )
    : 0;

  return (
    <>
    <Dialog
      open={!!orderId}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          requestClose();
        }
      }}
      fullWidth
      maxWidth="sm"
      className="order-details"
      PaperProps={{
        className: "order-details__dialog",
      }}
    >
      <div className="order-details__content">
        <div className="order-details__scroll">
        <div className="order-details__header">
          <div className="order-details__header-text">
            {order ? (
              <CopyableOrderId
                orderId={order.idZakaz || order.id}
                className="order-details__order-id"
              />
            ) : (
              <h2 className="order-details__title">Заказ</h2>
            )}
            {isEditable && isDirty && (
              <span className="order-details__dirty-badge">
                Есть несохранённые изменения
              </span>
            )}
          </div>
          <IconButton
            className="order-details__close"
            onClick={requestClose}
            aria-label="Закрыть"
          >
            <CloseIcon />
          </IconButton>
        </div>

        {loading && (
          <div className="order-details__state">Загрузка заказа...</div>
        )}

        {error && !loading && (
          <div className="order-details__state order-details__state--error">
            <p>{error}</p>
            <div className="order-details__state-actions">
              <button type="button" onClick={() => void loadOrder()}>
                Повторить
              </button>
              <button
                type="button"
                className="order-details__state-btn--secondary"
                onClick={requestClose}
              >
                Закрыть
              </button>
            </div>
          </div>
        )}

        {order && status && !loading && (
          <>
            <div
              className="order-details__status"
              style={{ backgroundColor: status.bg }}
            >
              <span
                className="order-details__status-text"
                style={{ color: status.color }}
              >
                {status.text}
              </span>
            </div>

            {actionError && (
              <div className="order-details__action-error">{actionError}</div>
            )}
            {actionNotice && (
              <div className="order-details__action-notice">{actionNotice}</div>
            )}

            <div className="order-details__info">
              <div className="order-details__info-row">
                <span className="order-details__info-label">Дата:</span>
                <span className="order-details__info-value">{date}</span>
              </div>
              {!isEditable && (
                <div className="order-details__info-row">
                  <span className="order-details__info-label">Оплата:</span>
                  <span className="order-details__info-value">
                    {paymentMethods[order.paymentMethod] || order.paymentMethod}
                  </span>
                </div>
              )}
            </div>

            {isEditable && !isPaid && (
              <div className="order-details__section">
                <h3 className="order-details__section-title">Способ оплаты</h3>
                <div className="order-details__delivery-type">
                  <button
                    type="button"
                    className={`order-details__delivery-btn ${
                      paymentMethodDraft === "yookassa"
                        ? "order-details__delivery-btn--active"
                        : ""
                    }`}
                    onClick={() => setPaymentMethodDraft("yookassa")}
                  >
                    Онлайн-оплата
                  </button>
                  <button
                    type="button"
                    className={`order-details__delivery-btn ${
                      paymentMethodDraft === "cash"
                        ? "order-details__delivery-btn--active"
                        : ""
                    }`}
                    onClick={() => setPaymentMethodDraft("cash")}
                  >
                    {cashPaymentLabel}
                  </button>
                </div>
              </div>
            )}

            <div className="order-details__section">
              <h3 className="order-details__section-title">Доставка</h3>
              {isEditable ? (
                <>
                  {citiesLoading ? (
                    <p className="order-details__hint">Загрузка точек...</p>
                  ) : cities.length > 0 ? (
                    <select
                      className="order-details__select"
                      value={selectedCashId}
                      onChange={(e) => {
                        const nextCashId = e.target.value;
                        setSelectedCashId(nextCashId);
                        setDistrictId("");
                        const nextCity = cities.find((c) => c.id === nextCashId);
                        if (nextCity?.onlyPickup) {
                          setPickup(true);
                        }
                      }}
                    >
                      <option value="">Выберите точку</option>
                      {cities.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name}
                          {city.address ? ` — ${city.address}` : ""}
                        </option>
                      ))}
                    </select>
                  ) : null}

                  <div className="order-details__delivery-type">
                    <button
                      type="button"
                      className={`order-details__delivery-btn ${
                        pickup ? "order-details__delivery-btn--active" : ""
                      }`}
                      onClick={() => setPickup(true)}
                    >
                      Самовывоз
                    </button>
                    <button
                      type="button"
                      className={`order-details__delivery-btn ${
                        !pickup ? "order-details__delivery-btn--active" : ""
                      } ${
                        onlyPickup
                          ? "order-details__delivery-btn--disabled"
                          : ""
                      }`}
                      onClick={() => {
                        if (!onlyPickup) setPickup(false);
                      }}
                      disabled={onlyPickup}
                    >
                      Доставка
                    </button>
                  </div>
                  {onlyPickup && (
                    <p className="order-details__hint order-details__hint--pickup-only">
                      Для этой точки доступен только самовывоз
                    </p>
                  )}
                  {!pickup && (
                    <>
                      {districtsLoading && (
                        <p className="order-details__hint">Загрузка районов...</p>
                      )}
                      {!districtsLoading && districtsError && (
                        <p className="order-details__hint order-details__hint--error">
                          {districtsError}
                        </p>
                      )}
                      {!districtsLoading && !districtsError && districts.length > 0 && (
                        <select
                          className="order-details__select"
                          value={districtId}
                          onChange={(e) => setDistrictId(e.target.value)}
                        >
                          <option value="">Выберите район</option>
                          {districts.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.name}
                            </option>
                          ))}
                        </select>
                      )}
                      <input
                        className="order-details__input"
                        placeholder="Адрес доставки"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                      />
                      <textarea
                        className="order-details__textarea"
                        placeholder="Комментарий"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={2}
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  <p className="order-details__info-value">
                    {order.pickup ? "Самовывоз" : "Доставка"}
                  </p>
                  {order.districtName && (
                    <p className="order-details__hint">Район: {order.districtName}</p>
                  )}
                  {order.deliveryAddress && (
                    <p className="order-details__hint">{order.deliveryAddress}</p>
                  )}
                </>
              )}
            </div>

            <div className="order-details__items">
              <h3 className="order-details__section-title">Состав заказа</h3>
              <div className="order-details__items-list">
                {order.items.map((item, index) => {
                  const key = getItemKey(item);
                  const qty = resolveItemQty(item);
                  const imageUrl = formatPhotoUrl(item.product.photo);
                  const pricing = resolveProductPrice(item.product, Math.max(qty, 1));
                  const lineTotal = qty > 0 ? getLineTotal(item.product, qty) : 0;
                  const canRemove =
                    isEditable && remainingItemsCount > 1;

                  if (isEditable && qty <= 0) {
                    return (
                      <div
                        key={key || index}
                        className="order-details__item order-details__item--removed"
                      >
                        <div className="order-details__item-body">
                          <span
                            className="order-details__item-name"
                            title={item.product.name}
                          >
                            {item.product.name}
                          </span>
                          <span className="order-details__removed-label">
                            Будет удалено
                          </span>
                          <button
                            type="button"
                            className="order-details__undo-btn"
                            onClick={() =>
                              setItemQty((prev) => ({
                                ...prev,
                                [key]: Math.max(item.minQuantity ?? 1, 1),
                              }))
                            }
                          >
                            Вернуть
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={key || index} className="order-details__item">
                      <div className="order-details__item-media">
                        <img
                          src={imageUrl}
                          alt={item.product.name}
                          className="order-details__item-image"
                          onError={handleImageError}
                        />
                      </div>
                      <div className="order-details__item-body">
                        <span
                          className="order-details__item-name"
                          title={item.product.name}
                        >
                          {item.product.name}
                        </span>
                        <span className="order-details__item-unit">
                          {formatUnitPriceLabel(item.product, qty)}
                        </span>
                        {pricing.wholesaleLabel && (
                          <span className="order-details__wholesale">
                            {pricing.wholesaleLabel}
                          </span>
                        )}
                        {isEditable ? (
                          <>
                            <div className="order-details__qty-controls">
                              <button
                                className="order-details__qty-btn"
                                type="button"
                                onClick={() => {
                                  if (qty <= 1) {
                                    if (!canRemove) {
                                      setActionError(
                                        "В заказе должна остаться хотя бы одна позиция"
                                      );
                                      return;
                                    }
                                    setItemQty((prev) => ({
                                      ...prev,
                                      [key]: 0,
                                    }));
                                    return;
                                  }
                                  setItemQty((prev) => ({
                                    ...prev,
                                    [key]: qty - 1,
                                  }));
                                }}
                                aria-label={
                                  qty <= 1 ? "Удалить позицию" : "Уменьшить"
                                }
                              >
                                −
                              </button>
                              <span className="order-details__qty-value">
                                {qty}
                              </span>
                              <button
                                className="order-details__qty-btn"
                                type="button"
                                onClick={() =>
                                  setItemQty((prev) => ({
                                    ...prev,
                                    [key]: qty + 1,
                                  }))
                                }
                                aria-label="Увеличить"
                              >
                                +
                              </button>
                            </div>
                            {canRemove && (
                              <button
                                type="button"
                                className="order-details__remove-btn"
                                onClick={() => {
                                  setItemQty((prev) => ({
                                    ...prev,
                                    [key]: 0,
                                  }));
                                }}
                              >
                                Удалить
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="order-details__item-quantity">
                            x{qty}
                          </span>
                        )}
                        <span className="order-details__item-total">
                          {lineTotal.toLocaleString("ru-RU")} ₽
                        </span>
                      </div>
                    </div>
                  );
                })}
                {pendingAdds.map((item) => {
                  const lineTotal = getLineTotal(item.product, item.quantity);
                  const imageUrl = formatPhotoUrl(item.product.photo);
                  return (
                    <div
                      key={`pending-${item.product.id}`}
                      className="order-details__item order-details__item--pending"
                    >
                      <div className="order-details__item-media">
                        <img
                          src={imageUrl}
                          alt={item.product.name}
                          className="order-details__item-image"
                          onError={handleImageError}
                        />
                      </div>
                      <div className="order-details__item-body">
                        <span
                          className="order-details__item-name"
                          title={item.product.name}
                        >
                          {item.product.name}
                        </span>
                        <span className="order-details__pending-badge">Новое</span>
                        <span className="order-details__item-unit">
                          {formatUnitPriceLabel(item.product, item.quantity)}
                        </span>
                        <div className="order-details__qty-controls">
                          <button
                            className="order-details__qty-btn"
                            type="button"
                            onClick={() => {
                              if (
                                item.quantity <= 1 &&
                                remainingItemsCount <= 1
                              ) {
                                setActionError(
                                  "В заказе должна остаться хотя бы одна позиция"
                                );
                                return;
                              }
                              setPendingAdds((prev) =>
                                prev
                                  .map((row) =>
                                    sameProductId(row.product.id, item.product.id)
                                      ? {
                                          ...row,
                                          quantity: row.quantity - 1,
                                        }
                                      : row
                                  )
                                  .filter((row) => row.quantity > 0)
                              );
                            }}
                            aria-label={
                              item.quantity <= 1
                                ? "Убрать новый товар"
                                : "Уменьшить"
                            }
                          >
                            −
                          </button>
                          <span className="order-details__qty-value">
                            {item.quantity}
                          </span>
                          <button
                            className="order-details__qty-btn"
                            type="button"
                            onClick={() =>
                              setPendingAdds((prev) =>
                                prev.map((row) =>
                                  sameProductId(row.product.id, item.product.id)
                                    ? { ...row, quantity: row.quantity + 1 }
                                    : row
                                )
                              )
                            }
                            aria-label="Увеличить"
                          >
                            +
                          </button>
                        </div>
                        {remainingItemsCount > 1 && (
                          <button
                            type="button"
                            className="order-details__remove-btn"
                            onClick={() =>
                              setPendingAdds((prev) =>
                                prev.filter(
                                  (row) =>
                                    !sameProductId(row.product.id, item.product.id)
                                )
                              )
                            }
                          >
                            Удалить
                          </button>
                        )}
                        <span className="order-details__item-total">
                          {lineTotal.toLocaleString("ru-RU")} ₽
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {isEditable && (
                <>
                  <button
                    type="button"
                    className="order-details__button order-details__button--add"
                    onClick={() => setPickerOpen(true)}
                    disabled={saving || paying || cancelling}
                  >
                    + Добавить товар
                  </button>
                  <p className="order-details__hint order-details__hint--items">
                    Состав можно менять до оплаты. Новые позиции сохранятся вместе
                    с остальными изменениями.
                  </p>
                </>
              )}
            </div>
          </>
        )}
        </div>

        {order && !loading && !error && (
          <div
            className={`order-details__sticky-bar ${
              isEditable ? "order-details__sticky-bar--editable" : ""
            }`}
          >
            <div className="order-details__summary order-details__summary--sticky">
              <div className="order-details__summary-row order-details__summary-row--total">
                <span>Итого:</span>
                <span className="order-details__total">
                  {(isEditable ? displayTotal : order.totalAmount).toLocaleString(
                    "ru-RU"
                  )}{" "}
                  ₽
                </span>
              </div>
            </div>

            {isEditable && (
              <div className="order-details__actions">
                {isDirty && (
                  <button
                    type="button"
                    className="order-details__button order-details__button--dirty"
                    onClick={() => void handleSaveAll()}
                    disabled={saving || cancelling || paying}
                  >
                    {saving ? "Сохранение..." : "Сохранить изменения"}
                  </button>
                )}
                {canPay && isDirty && (
                  <button
                    type="button"
                    className="order-details__button order-details__button--pay"
                    onClick={() => void handleSaveAndPay()}
                    disabled={saving || paying || cancelling}
                  >
                    {paying || saving
                      ? "Подготовка..."
                      : "Сохранить и оплатить"}
                  </button>
                )}
                {canPay && !isDirty && (
                  <button
                    type="button"
                    className="order-details__button order-details__button--pay"
                    onClick={() => void handlePay()}
                    disabled={paying || saving || cancelling}
                  >
                    {paying
                      ? "Подготовка оплаты..."
                      : order.paymentUrl
                      ? "Открыть ссылку оплаты"
                      : "Оплатить заказ"}
                  </button>
                )}
                <button
                  type="button"
                  className="order-details__button order-details__button--cancel"
                  onClick={() => void handleCancelOrder()}
                  disabled={cancelling || saving || paying || isDirty}
                >
                  {cancelling ? "Отмена..." : "Отменить заказ"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Dialog>

      <OrderAddItemsPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        cashId={selectedCashId || order?.cashId}
        outletId={outletId || order?.outletId}
        onPick={handlePickProduct}
      />
    </>
  );
};
